import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Avatar, Button, Drawer, Empty, Skeleton, Tag, Tooltip, Typography } from 'antd';
import {
  ApiOutlined,
  AppstoreOutlined,
  CloseCircleOutlined,
  EditOutlined,
  KeyOutlined,
  LinkOutlined,
  LoginOutlined,
  LogoutOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  StopOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import type { AdminUser, AdminUserOverview, SpaceRole } from '@lens/shared';
import { SpaceAvatar } from '../../shared/components/SpaceAvatar.tsx';
import { ApiError } from '../../shared/api/client.ts';
import { roleBadge } from '../../shared/theme/tokens.ts';
import { getAdminUserOverview } from './api.ts';
import './AdminUserCard.css';
import {
  describeUserAgent,
  formatAgo,
  formatDate,
  formatDateTime,
  formatShortDate,
  presenceOf,
  PRESENCE_COLOR,
  PRESENCE_LABEL,
} from './userPresence.ts';

const { Text } = Typography;

const SPACE_ROLE_LABELS: Record<SpaceRole, string> = {
  admin: 'Администратор',
  member: 'Участник',
  viewer: 'Наблюдатель',
};

const LOGIN_EVENT_META: Record<string, { label: string; icon: ReactNode; tone: string }> = {
  login_success: { label: 'Вход по паролю', icon: <LoginOutlined />, tone: 'var(--color-success)' },
  sso_login: { label: 'Вход через SSO', icon: <SafetyCertificateOutlined />, tone: 'var(--color-success)' },
  sso_linked: { label: 'Первый вход через SSO', icon: <SafetyCertificateOutlined />, tone: 'var(--color-success)' },
  sso_provisioned: { label: 'Заведён при входе через SSO', icon: <UserAddOutlined />, tone: 'var(--color-primary)' },
  login_failure: { label: 'Неудачная попытка входа', icon: <CloseCircleOutlined />, tone: 'var(--color-error)' },
  sso_denied_no_access: { label: 'SSO: нет роли доступа', icon: <StopOutlined />, tone: 'var(--color-error)' },
  logout: { label: 'Выход', icon: <LogoutOutlined />, tone: 'var(--color-text-secondary)' },
  password_change: { label: 'Смена пароля', icon: <KeyOutlined />, tone: 'var(--color-warning)' },
  password_reset_by_admin: { label: 'Пароль сброшен админом', icon: <KeyOutlined />, tone: 'var(--color-warning)' },
};

// ─── Мелкие строительные блоки ──────────────────────────────────────────

function Section({
  title,
  extra,
  children,
}: {
  title: string;
  extra?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="uc-section">
      <div className="uc-section-head">
        <span className="uc-section-title">{title}</span>
        {extra && <span className="uc-section-extra">{extra}</span>}
      </div>
      {children}
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  tooltip,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tooltip?: string;
}): JSX.Element {
  const body = (
    <div className="uc-stat">
      <div className="uc-stat-label">{label}</div>
      <div className="uc-stat-value">{value}</div>
      {hint && <div className="uc-stat-hint">{hint}</div>}
    </div>
  );
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
}

function StatusPill({ on, onText = 'Подключено', offText = 'Не подключено' }: {
  on: boolean;
  onText?: string;
  offText?: string;
}): JSX.Element {
  return (
    <span className={`uc-pill ${on ? 'uc-pill-on' : 'uc-pill-off'}`}>
      <span className="uc-pill-dot" />
      {on ? onText : offText}
    </span>
  );
}

function Connection({
  icon,
  title,
  on,
  onText,
  children,
}: {
  icon: ReactNode;
  title: string;
  on: boolean;
  onText?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className={`uc-conn ${on ? 'uc-conn-on' : ''}`}>
      <div className="uc-conn-head">
        <span className="uc-conn-icon">{icon}</span>
        <span className="uc-conn-title">{title}</span>
        <StatusPill on={on} onText={onText} />
      </div>
      {children && <div className="uc-conn-body">{children}</div>}
    </div>
  );
}

// ─── Календарь активности ───────────────────────────────────────────────

const WEEKS = 26;
const WEEKDAY_LABELS = ['пн', '', 'ср', '', 'пт', '', ''];

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Уровень заливки 0..4 относительно самого активного дня периода. */
function levelOf(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  const r = count / max;
  if (r <= 0.25) return 1;
  if (r <= 0.5) return 2;
  if (r <= 0.75) return 3;
  return 4;
}

function ActivityCalendar({ daily }: { daily: AdminUserOverview['daily'] }): JSX.Element {
  const { weeks, months, total, activeDays } = useMemo(() => {
    const byDay = new Map(daily.map((d) => [d.date, d.count]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Начало — понедельник недели, отстоящей на WEEKS-1 назад.
    const start = new Date(today);
    const weekday = (today.getDay() + 6) % 7; // пн = 0
    start.setDate(today.getDate() - weekday - (WEEKS - 1) * 7);

    const max = Math.max(0, ...daily.map((d) => d.count));
    const cols: Array<Array<{ key: string; date: Date; count: number; level: number; future: boolean }>> = [];
    const monthMarks: Array<{ col: number; label: string }> = [];
    let lastMonth = -1;
    for (let w = 0; w < WEEKS; w++) {
      const col = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(start);
        date.setDate(start.getDate() + w * 7 + d);
        const key = dayKey(date);
        const count = byDay.get(key) ?? 0;
        col.push({ key, date, count, level: levelOf(count, max), future: date > today });
      }
      const month = col[0]!.date.getMonth();
      // Первая неделя может задеть хвост прошлого месяца — такую подпись
      // пропускаем, иначе она налезет на следующую.
      const firstOfNext = new Date(col[0]!.date.getFullYear(), month + 1, 1);
      const tooShort = w === 0 && (firstOfNext.getTime() - col[0]!.date.getTime()) / 86_400_000 < 14;
      if (month !== lastMonth && !tooShort) {
        monthMarks.push({
          col: w,
          label: col[0]!.date.toLocaleDateString('ru-RU', { month: 'short' }).replace('.', ''),
        });
        lastMonth = month;
      }
      cols.push(col);
    }
    return {
      weeks: cols,
      months: monthMarks,
      total: daily.reduce((s, d) => s + d.count, 0),
      activeDays: daily.filter((d) => d.count > 0).length,
    };
  }, [daily]);

  return (
    <div className="uc-cal">
      <div className="uc-cal-summary">
        <b>{total}</b> {plural(total, 'действие', 'действия', 'действий')} ·{' '}
        <b>{activeDays}</b> {plural(activeDays, 'активный день', 'активных дня', 'активных дней')} за полгода
      </div>
      <div className="uc-cal-grid" role="img" aria-label={`Активность за полгода: ${total} действий`}>
        <div className="uc-cal-months">
          {months.map((m) => (
            <span key={`${m.col}-${m.label}`} style={{ gridColumn: m.col + 1 }}>
              {m.label}
            </span>
          ))}
        </div>
        <div className="uc-cal-weekdays">
          {WEEKDAY_LABELS.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
        <div className="uc-cal-cells">
          {weeks.map((col, w) => (
            <div key={w} className="uc-cal-col">
              {col.map((cell) =>
                cell.future ? (
                  <span key={cell.key} className="uc-cal-cell uc-cal-future" />
                ) : (
                  <Tooltip
                    key={cell.key}
                    mouseEnterDelay={0}
                    title={
                      <>
                        <b>
                          {cell.count === 0
                            ? 'Нет действий'
                            : `${cell.count} ${plural(cell.count, 'действие', 'действия', 'действий')}`}
                        </b>
                        <br />
                        {cell.date.toLocaleDateString('ru-RU', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'long',
                        })}
                      </>
                    }
                  >
                    <span className={`uc-cal-cell uc-cal-l${cell.level}`} />
                  </Tooltip>
                ),
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="uc-cal-legend">
        меньше
        {[0, 1, 2, 3, 4].map((l) => (
          <span key={l} className={`uc-cal-cell uc-cal-l${l}`} />
        ))}
        больше
      </div>
    </div>
  );
}

// ─── Карточка ───────────────────────────────────────────────────────────

export interface AdminUserDrawerProps {
  user: AdminUser | null;
  onClose: () => void;
  onEdit: (user: AdminUser) => void;
  onSpaces: (user: AdminUser) => void;
  onResetPassword: (user: AdminUser) => void;
  /** Меняется после правок пользователя — карточка перечитывает данные. */
  reloadKey?: number;
}

export function AdminUserDrawer({
  user,
  onClose,
  onEdit,
  onSpaces,
  onResetPassword,
  reloadKey,
}: AdminUserDrawerProps): JSX.Element {
  const [data, setData] = useState<AdminUserOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setData((prev) => (prev?.user.id === userId ? prev : null));
    getAdminUserOverview(userId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Не удалось загрузить карточку');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  // Шапку рисуем сразу по строке списка, остальное — когда придёт карточка.
  const u = data?.user ?? user;
  const presence = presenceOf(u?.lastSeenAt);
  const maxSpaceChanges = Math.max(1, ...(data?.spaces.map((s) => s.changes) ?? [0]));

  return (
    <Drawer
      open={!!user}
      onClose={onClose}
      width="min(680px, 100vw)"
      title={null}
      closable
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      rootClassName="uc-drawer"
    >
      {u && (
        <div className="uc-root" data-testid="admin-user-card">
          <header className="uc-hero">
            <div className="uc-avatar-wrap">
              <Avatar size={72} src={u.avatarUrl ?? undefined} className="uc-avatar">
                {(u.displayName || u.login).slice(0, 1).toUpperCase()}
              </Avatar>
              <Tooltip title={PRESENCE_LABEL[presence]}>
                <span className="uc-presence-dot" style={{ background: PRESENCE_COLOR[presence] }} />
              </Tooltip>
            </div>
            <div className="uc-hero-main">
              <div className="uc-name">{u.displayName}</div>
              <div className="uc-sub">
                <span>@{u.login}</span>
                {u.email && (
                  <>
                    <span className="uc-sep">·</span>
                    <a href={`mailto:${u.email}`}>{u.email}</a>
                  </>
                )}
              </div>
              <div className="uc-tags">
                {u.isPlatformAdmin ? (
                  <Tag bordered={false} style={{ background: roleBadge.admin.bg, color: roleBadge.admin.color }}>
                    Администратор платформы
                  </Tag>
                ) : (
                  <Tag bordered={false}>Пользователь</Tag>
                )}
                {u.authMethod && (
                  <Tag bordered={false} icon={u.authMethod === 'sso' ? <SafetyCertificateOutlined /> : <KeyOutlined />}>
                    {u.authMethod === 'sso' ? 'Вход через SSO' : 'Вход по паролю'}
                  </Tag>
                )}
                {!u.isActive && (
                  <Tag bordered={false} color="error" icon={<StopOutlined />}>
                    Отключён
                  </Tag>
                )}
              </div>
              <div className="uc-presence-line" style={{ color: PRESENCE_COLOR[presence] === 'var(--color-border)' ? 'var(--color-text-secondary)' : undefined }}>
                <span className="uc-presence-mini" style={{ background: PRESENCE_COLOR[presence] }} />
                {presence === 'never'
                  ? 'Ни одного захода в Lens'
                  : presence === 'online'
                    ? 'Сейчас в Lens'
                    : `Последний визит — ${formatAgo(u.lastSeenAt)}`}
              </div>
            </div>
            <div className="uc-actions">
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(u)}>
                Изменить
              </Button>
              <Button size="small" icon={<AppstoreOutlined />} onClick={() => onSpaces(u)}>
                Доступ
              </Button>
              {u.authMethod !== 'sso' && (
                <Button size="small" icon={<KeyOutlined />} onClick={() => onResetPassword(u)}>
                  Пароль
                </Button>
              )}
            </div>
          </header>

          {error && <Alert type="error" showIcon message={error} style={{ margin: 20 }} />}

          {!data && !error ? (
            <div style={{ padding: 24 }}>
              <Skeleton active paragraph={{ rows: 10 }} />
            </div>
          ) : data ? (
            <div className="uc-body">
              <div className="uc-stats">
                <Stat
                  label="Последний визит"
                  value={formatAgo(data.user.lastSeenAt)}
                  tooltip={formatDateTime(data.user.lastSeenAt)}
                  hint="запрос из браузера"
                />
                <Stat
                  label="Последний вход"
                  value={formatAgo(data.user.lastLoginAt)}
                  tooltip={formatDateTime(data.user.lastLoginAt)}
                  hint={data.user.authMethod === 'sso' ? 'через SSO' : 'паролем'}
                />
                <Stat
                  label={`Входов за ${data.periodDays} дней`}
                  value={data.logins.success}
                  hint={
                    data.logins.failed > 0 ? (
                      <span style={{ color: 'var(--color-error)' }}>
                        {data.logins.failed} {plural(data.logins.failed, 'неудачная', 'неудачные', 'неудачных')}
                      </span>
                    ) : (
                      'без неудачных'
                    )
                  }
                />
                <Stat label="В Lens с" value={formatShortDate(data.user.createdAt)} hint="дата заведения" />
              </div>

              <Section title="Активность">
                <ActivityCalendar daily={data.daily} />
                <div className="uc-counts">
                  <Stat label="Изменений" value={data.counts.changes} />
                  <Stat label="Новых записей" value={data.counts.itemsCreated} />
                  <Stat label="Комментариев" value={data.counts.comments} />
                  <Stat label="Запросов к ИИ" value={data.counts.aiRequests} />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Счётчики — за последние {data.periodDays} дней.
                </Text>
              </Section>

              <Section title="Подключения">
                <div className="uc-conns">
                  <Connection
                    icon={<ApiOutlined />}
                    title="MCP"
                    on={data.tokens.length > 0}
                    onText={
                      data.tokens.length > 0
                        ? `${data.tokens.length} ${plural(data.tokens.length, 'токен', 'токена', 'токенов')}`
                        : undefined
                    }
                  >
                    {data.tokens.length === 0 ? (
                      <Text type="secondary">Токенов нет — MCP не настроен</Text>
                    ) : (
                      <>
                        <div>
                          Использовался:{' '}
                          <b>{data.user.mcpLastUsedAt ? formatAgo(data.user.mcpLastUsedAt) : 'ни разу'}</b>
                        </div>
                        <ul className="uc-tokens">
                          {data.tokens.map((t) => (
                            <li key={t.id}>
                              <Tooltip
                                title={`Создан ${formatDate(t.createdAt)}${t.expiresAt ? `, действует до ${formatDate(t.expiresAt)}` : ', бессрочный'}`}
                              >
                                <span className="uc-token-name">{t.name}</span>
                              </Tooltip>
                              <code>{t.prefix}…</code>
                              <span className="uc-token-used">
                                {t.lastUsedAt ? formatAgo(t.lastUsedAt) : 'не использовался'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Connection>
                  <Connection
                    icon={<SendOutlined />}
                    title="Telegram"
                    on={!!data.telegram}
                    onText={data.telegram?.username ? `@${data.telegram.username}` : undefined}
                  >
                    {data.telegram ? (
                      <>
                        {data.telegram.name && <div>{data.telegram.name}</div>}
                        <div>
                          Привязан: <b>{formatDate(data.telegram.linkedAt)}</b>
                        </div>
                        <div>
                          Последнее сообщение боту: <b>{formatAgo(data.telegram.lastSeenAt)}</b>
                        </div>
                        <div data-testid="admin-user-tg-start">
                          «Start» у бота:{' '}
                          <b>
                            {data.telegram.botChatOpenedAt
                              ? formatDate(data.telegram.botChatOpenedAt)
                              : 'не нажат — бот не может писать'}
                          </b>
                        </div>
                        <div data-testid="admin-user-tg-notify">
                          Уведомления:{' '}
                          <b>
                            {!data.telegram.notifyEnabled
                              ? 'выключены'
                              : data.telegram.mutedSpaces.length > 0
                                ? `выключены в: ${data.telegram.mutedSpaces.join(', ')}`
                                : 'включены'}
                          </b>
                        </div>
                      </>
                    ) : (
                      <Text type="secondary">Записи из чатов останутся без автора</Text>
                    )}
                  </Connection>
                  <Connection icon={<LinkOutlined />} title="Jira" on={!!data.user.jiraConnected}>
                    <Text type="secondary">
                      {data.user.jiraConnected
                        ? 'Личный токен Jira задан — синхронизация от его имени'
                        : 'Личный токен Jira не задан'}
                    </Text>
                  </Connection>
                  <Connection
                    icon={<RobotOutlined />}
                    title="Ключ LLM"
                    on={!!data.user.llmKeyConnected}
                    onText="Задан"
                  >
                    <Text type="secondary">
                      {data.user.llmKeyConnected
                        ? 'Может пользоваться ИИ-помощником'
                        : 'Без ключа ИИ-помощник недоступен'}
                    </Text>
                  </Connection>
                </div>
              </Section>

              <Section
                title="Пространства"
                extra={`${data.user.spacesCount ?? 0} · изменения за ${data.periodDays} дней`}
              >
                {data.spaces.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Не состоит ни в одном пространстве" />
                ) : (
                  <div className="uc-spaces">
                    {data.spaces.map((s) => (
                      <a key={s.spaceId} className="uc-space" href={`/s/${s.slug}`}>
                        <SpaceAvatar name={s.name} icon={s.icon} seed={s.spaceId} size={24} />
                        <span className="uc-space-name">
                          {s.name}
                          {s.isArchived && <span className="uc-muted"> · в архиве</span>}
                        </span>
                        <span className="uc-space-role">{s.role ? SPACE_ROLE_LABELS[s.role] : 'не участник'}</span>
                        <span className="uc-space-bar">
                          <span style={{ width: `${(s.changes / maxSpaceChanges) * 100}%` }} />
                        </span>
                        <span className="uc-space-count">{s.changes}</span>
                      </a>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="История входов" extra="последние 12 событий">
                {data.logins.recent.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Входов в журнале нет" />
                ) : (
                  <ol className="uc-timeline">
                    {data.logins.recent.map((e, i) => {
                      const meta = LOGIN_EVENT_META[e.event] ?? {
                        label: e.event,
                        icon: <LoginOutlined />,
                        tone: 'var(--color-text-secondary)',
                      };
                      return (
                        <li key={`${e.createdAt}-${i}`}>
                          <span className="uc-tl-icon" style={{ color: meta.tone }}>
                            {meta.icon}
                          </span>
                          <span className="uc-tl-main">
                            <span className="uc-tl-label">{meta.label}</span>
                            <span className="uc-tl-meta">
                              {describeUserAgent(e.userAgent)}
                              {e.ip && <> · {e.ip}</>}
                            </span>
                          </span>
                          <Tooltip title={formatDateTime(e.createdAt)}>
                            <span className="uc-tl-time">{formatAgo(e.createdAt)}</span>
                          </Tooltip>
                        </li>
                      );
                    })}
                  </ol>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Журнал входов хранится 180 дней.
                </Text>
              </Section>
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
